import {useSafeChatManager} from '@github-ui/copilot-chat/utils/CopilotChatManagerContext'
import {useSafeChatState} from '@github-ui/copilot-chat-contexts/ChatStateContext'
import {getSelectedThread} from '@github-ui/copilot-chat-state/get-selected-thread'
import {sendEvent} from '@github-ui/hydro-analytics'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {useLocation} from '@github-ui/react-router'
import {BrowserIcon, DiceIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {useEffect, useRef, useState} from 'react'

import type {CopilotImmersivePayload, Icebreaker} from '../../routes/payloads'
import styles from './EmptyState.module.css'

interface EmptyStateProps {
  isOpening: boolean
  /** Hosts without icebreakers (e.g. the Agents workbench) opt out of the suggestion button. */
  hideIcebreakerSuggestion?: boolean
}

type IcebreakerType = 'functional' | 'instructional' | 'interactional'

interface IcebreakerData {
  type: IcebreakerType
  data: Icebreaker[]
}

function isIcebreakerDataArray(value: unknown): value is IcebreakerData[] {
  return Array.isArray(value) && value.every(item => 'type' in item && 'data' in item)
}

function getRandomIcebreaker(icebreakers: Icebreaker[]): Icebreaker {
  const randomIndex = Math.floor(Math.random() * icebreakers.length)
  const randomIcebreaker = icebreakers[randomIndex]
  if (!randomIcebreaker) {
    throw new Error('No icebreaker found')
  }
  return randomIcebreaker
}

export function EmptyState({isOpening, hideIcebreakerSuggestion = false}: EmptyStateProps) {
  const manager = useSafeChatManager()
  const state = useSafeChatState()
  const location = useLocation()
  const buttonRef = useRef<HTMLButtonElement>(null)

  const currentThread = state ? getSelectedThread(state) : null
  // AppPayloadContext defaults to undefined, so a host outside the immersive app has no payload.
  const payload = useAppPayload<CopilotImmersivePayload | undefined>()

  const [interactionalSuggestions, setInteractionalSuggestions] = useState<Icebreaker[]>([])

  useEffect(() => {
    const payloadIcebreakers = payload?.icebreakers
    if (isIcebreakerDataArray(payloadIcebreakers)) {
      const icebreakers = payloadIcebreakers.find(ib => ib.type === 'interactional')
      if (icebreakers && icebreakers.data.length > 0) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setInteractionalSuggestions(icebreakers.data)
      }
    }
  }, [payload?.icebreakers, location])

  // Focus the button when isOpening becomes true
  useEffect(() => {
    if (isOpening && buttonRef.current) {
      // Use requestAnimationFrame to ensure focus happens after render
      window.requestAnimationFrame(() => {
        buttonRef.current?.focus({preventScroll: true})
      })
    }
  }, [isOpening])

  const handleButtonClick = () => {
    if (!state || !manager || interactionalSuggestions.length === 0) return

    const randomSuggestion = getRandomIcebreaker(interactionalSuggestions)
    const thread = state.messages.length === 0 ? currentThread : getSelectedThread(state)
    const currentUserLogin = state.currentUserLogin
    const message = randomSuggestion.message

    void manager.sendChatMessage({
      thread,
      content: message.replaceAll('$$USERNAME$$', currentUserLogin),
      references: state.currentReferences,
      topic: state.currentTopic,
      context: state.context,
      customInstructions: state.customInstructions,
      model: state.model,
    })

    sendEvent('dotcom_chat.activate', {
      target: 'CONTENT_PREVIEW_EMPTY_STATE_ICEBREAKER_BUTTON',
      topic: state.currentTopic?.name,
      mode: 'immersive',
      suggestionId: randomSuggestion.id,
    })
  }

  const showIcebreakerButton = !hideIcebreakerSuggestion && !!state && !!manager

  return (
    <div className={styles.container}>
      <BrowserIcon size={24} className={styles.icon} />
      <h1 className="sr-only">Content preview panel</h1>
      <h2 className={styles.heading}>View files directly within Copilot</h2>
      <p className={styles.subtitle}>
        {showIcebreakerButton
          ? 'Ask to generate a file or app, or use the button below to try it yourself.'
          : 'Files, diffs, and issues you open will appear here.'}
      </p>
      {showIcebreakerButton && (
        <Button
          ref={buttonRef}
          leadingVisual={DiceIcon}
          onClick={handleButtonClick}
          disabled={state.isWaitingOnCopilot}
        >
          I’m feeling lucky
        </Button>
      )}
    </div>
  )
}
